/**
 * Mention Service - Manage @mentions and tagging across deals, projects, and faults
 *
 * Core functions:
 * - Extract @mentions from text
 * - Create mention notifications
 * - Mark mentions as read
 * - Query mentions for a user
 */

import { db } from '@/firebase';
import {
  collection,
  updateDoc,
  setDoc,
  doc,
  getDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
} from 'firebase/firestore';
import {
  Mention,
  MentionEntityType,
  MentionStatus,
  MentionInput,
  MentionableUser,
} from '@/types/mention.types';
import { tenantIdReadUnion } from '@/lib/tenant';

// ============================================================================
// NAVIGATION
// ============================================================================

/**
 * Build the navigation URL for a mention click.
 *
 * Appends `?noteId={sourceNoteId}` (or `&noteId=...` if the linkPath already
 * has a query string) so the target page can scroll to and highlight the
 * specific note/timeline entry the mention came from. Falls back to the bare
 * linkPath when the mention has no source note (direct tags).
 */
export function buildMentionNavPath(mention: Pick<Mention, 'linkPath' | 'sourceNoteId'>): string | null {
  if (!mention.linkPath) return null;
  if (!mention.sourceNoteId) return mention.linkPath;
  const sep = mention.linkPath.includes('?') ? '&' : '?';
  return `${mention.linkPath}${sep}noteId=${encodeURIComponent(mention.sourceNoteId)}`;
}

// ============================================================================
// MENTION EXTRACTION
// ============================================================================

/**
 * Extract @mentions from text content
 *
 * Matches patterns like:
 * - @reuben
 * - @john.smith
 * - @casey_jones
 *
 * @param text - Text content to parse
 * @param availableUsers - List of users that can be mentioned
 * @returns Array of extracted mentions with user IDs
 */
export function extractMentions(
  text: string,
  availableUsers: MentionableUser[]
): MentionInput[] {
  const mentions: MentionInput[] = [];

  // Match @username (alphanumeric, dots, underscores, hyphens) — but only when
  // the "@" STARTS a word. The captured lead-in group is what enforces that:
  // without it, `reuben@thetechery.au` in a note body extracted "@thetechery"
  // and, worse, an address like `casey@…` fanned a real notification out to
  // Casey. Same word-start rule the composer's picker uses
  // (src/utils/mention-trigger.ts) so what you can type is what gets notified.
  const mentionRegex = /(^|[^\w@])@([\w.-]+)/g;
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    const lead = match[1] || '';
    const mentionText = match[2].toLowerCase();
    // Position of the "@" itself, not of the lead-in character.
    const atIndex = match.index + lead.length;

    // Find matching user (case-insensitive, matches firstName, lastName, or full name)
    const user = availableUsers.find((u) => {
      const nameLower = u.name.toLowerCase();
      const firstLower = u.firstName?.toLowerCase() || '';
      const lastLower = u.lastName?.toLowerCase() || '';

      return (
        nameLower === mentionText ||
        firstLower === mentionText ||
        lastLower === mentionText ||
        nameLower.replace(/\s+/g, '.') === mentionText ||
        nameLower.replace(/\s+/g, '_') === mentionText
      );
    });

    if (user) {
      mentions.push({
        userId: user.id,
        userName: user.name,
        startIndex: atIndex,
        endIndex: atIndex + 1 + match[2].length,
      });
    }
  }

  return mentions;
}

/**
 * Highlight @mentions in text with HTML spans
 *
 * @param text - Raw text with @mentions
 * @param mentions - Extracted mention data
 * @returns HTML string with highlighted mentions
 */
export function highlightMentions(text: string, mentions: MentionInput[]): string {
  if (mentions.length === 0) return text;

  let result = '';
  let lastIndex = 0;

  // Sort mentions by start index
  const sorted = [...mentions].sort((a, b) => a.startIndex - b.startIndex);

  sorted.forEach((mention) => {
    // Add text before mention
    result += text.slice(lastIndex, mention.startIndex);

    // Add highlighted mention
    const mentionText = text.slice(mention.startIndex, mention.endIndex);
    result += `<span class="mention-highlight" data-user-id="${mention.userId}">${mentionText}</span>`;

    lastIndex = mention.endIndex;
  });

  // Add remaining text
  result += text.slice(lastIndex);

  return result;
}

/**
 * Collapse raw @match positions down to ONE recipient per user.
 *
 * `extractMentions` returns one entry per @handle OCCURRENCE, and a user can
 * be matched by several handles in the same note ("@casey ... @jones ...") or
 * by the same handle twice. Fanning out per-occurrence is where most of the
 * "why am I tagged twice?" reports came from.
 */
export function uniqueMentionRecipients(
  text: string,
  availableUsers: MentionableUser[]
): { userId: string; userName: string }[] {
  const seen = new Set<string>();
  const recipients: { userId: string; userName: string }[] = [];

  for (const m of extractMentions(text, availableUsers)) {
    if (!m.userId || seen.has(m.userId)) continue;
    seen.add(m.userId);
    recipients.push({ userId: m.userId, userName: m.userName });
  }

  return recipients;
}

// ============================================================================
// MENTION CRUD
// ============================================================================

/** Local (not UTC) YYYY-MM-DD — `toISOString()` returns yesterday in AEST. */
function todayLocalKey(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
}

/** FNV-1a 32-bit, hex. Only ever used inside an already user+entity+day
 *  scoped key, so the collision space is a handful of documents. */
function hashText(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(36) + text.length.toString(36);
}

function idPart(value: string): string {
  return (value || 'x').replace(/[^A-Za-z0-9_-]/g, '_').slice(0, 64);
}

/**
 * Deterministic document id for a mention — the dedupe primitive.
 *
 * Two writes that describe the SAME tag land on the same document id, so a
 * double fan-out (two call sites for one note, a retry, a StrictMode double
 * render) collapses into one document instead of two bell entries. The
 * `onMentionCreated` CF only fires on create, so the duplicate write also
 * can't double-push.
 *
 * Keyed on the source note when there is one. Direct tags with no note fall
 * back to content + local day, so re-tagging the same person on the same
 * thing tomorrow is legitimately a new mention, but a double-click today
 * is not.
 */
export function mentionDocId(params: {
  tenantId: string;
  mentionedUserId: string;
  entityType: MentionEntityType | string;
  entityId: string;
  sourceNoteId?: string;
  noteContent?: string;
  /** Override for tests — defaults to today in local time. */
  dayKey?: string;
}): string {
  const base = [
    idPart(params.tenantId),
    idPart(params.mentionedUserId),
    idPart(String(params.entityType)),
    idPart(params.entityId),
  ].join('.');

  const suffix = params.sourceNoteId
    ? `n${idPart(params.sourceNoteId)}`
    : `d${params.dayKey ?? todayLocalKey()}${hashText(params.noteContent || '')}`;

  return `m.${base}.${suffix}`;
}

/**
 * Write one mention document at its deterministic id, skipping the write
 * entirely when that mention already exists.
 *
 * Skip-if-exists (rather than a blind overwrite) is what stops a re-fired
 * fan-out from resurrecting a mention the user has already read or cleared.
 *
 * ⚠️ The existence probe MUST stay fault-tolerant. `mentions/` is gated by
 * `belongsToCurrentTenant()`, which dereferences `resource.data.tenantId` —
 * and for a document that does not exist yet, `resource` is null, so the rule
 * errors out and the read is DENIED rather than returning an empty snapshot
 * (verified against the emulator: "Null value error" at firestore.rules L728).
 * A hard `await getDoc(...)` here therefore throws on every genuinely-new
 * mention and nothing is ever written. Treat a failed probe as "unknown" and
 * write: the deterministic id still collapses concurrent duplicates onto one
 * document, which is the part that actually stops double-tagging.
 *
 * @returns The document id if written, or null if it was a duplicate.
 */
async function writeMentionDoc(
  mention: Omit<Mention, 'id'>,
  sourceNoteId?: string
): Promise<string | null> {
  const id = mentionDocId({
    tenantId: mention.tenantId,
    mentionedUserId: mention.mentionedUserId,
    entityType: mention.entityType,
    entityId: mention.entityId,
    sourceNoteId,
    noteContent: mention.noteContent,
  });

  const ref = doc(db, 'mentions', id);
  const existing = await getDoc(ref).catch(() => null);
  if (existing?.exists()) return null;

  await setDoc(ref, mention);
  return id;
}

/**
 * Create mention notifications for all @mentioned users in a note
 *
 * One mention per user, deduped by deterministic document id — calling this
 * twice for the same note is a no-op the second time.
 *
 * @param params - Mention creation parameters
 * @returns Array of created mention IDs (duplicates are omitted)
 */
export async function createMentions(params: {
  noteContent: string;
  entityType: MentionEntityType;
  entityId: string;
  entityTitle: string;
  createdBy: string;
  createdByName: string;
  tenantId: string;
  linkPath?: string;
  availableUsers: MentionableUser[];
  /** ID of the underlying note / timeline entry — enables deep-link to the
   * exact entry inside the parent. Omit for direct tags without a note. */
  sourceNoteId?: string;
}): Promise<string[]> {
  const {
    noteContent,
    entityType,
    entityId,
    entityTitle,
    createdBy,
    createdByName,
    tenantId,
    linkPath,
    availableUsers,
    sourceNoteId,
  } = params;

  // One recipient per user — NOT one per @handle occurrence.
  const recipients = uniqueMentionRecipients(noteContent, availableUsers);

  if (recipients.length === 0) {
    return [];
  }

  // Create excerpt (first 100 chars)
  const noteExcerpt = noteContent.length > 100
    ? noteContent.slice(0, 100) + '...'
    : noteContent;

  const mentionIds: string[] = [];

  for (const recipient of recipients) {
    const mentionDoc: Omit<Mention, 'id'> = {
      tenantId,
      mentionedUserId: recipient.userId,
      mentionedUserName: recipient.userName,
      createdBy,
      createdByName,
      entityType,
      entityId,
      entityTitle,
      noteContent,
      noteExcerpt,
      status: MentionStatus.UNREAD,
      createdAt: new Date().toISOString(),
      ...(linkPath ? { linkPath } : {}),
      ...(sourceNoteId ? { sourceNoteId } : {}),
    };

    const id = await writeMentionDoc(mentionDoc, sourceNoteId);
    if (id) mentionIds.push(id);
  }

  return mentionIds;
}

/**
 * Create mention notifications for an EXPLICIT list of recipients — no text
 * extraction.
 *
 * Use this when the recipients are already known by userId (task assignment,
 * follow-ups, reminders). Routing through `createMentions` requires
 * synthesising "@firstname" handles into the text, and `extractMentions`
 * resolves a first name to the FIRST matching user — so any first-name
 * collision misroutes the notification, and @handles already present in the
 * content double-fire. This function writes one mentions/ doc per recipient
 * directly; the onMentionCreated CF fans out the bell + push as usual.
 *
 * @returns Array of created mention IDs
 */
export async function createDirectMentions(params: {
  recipients: { userId: string; userName: string }[];
  noteContent: string;
  entityType: MentionEntityType;
  entityId: string;
  entityTitle: string;
  createdBy: string;
  createdByName: string;
  tenantId: string;
  linkPath?: string;
  sourceNoteId?: string;
}): Promise<string[]> {
  const { recipients, noteContent } = params;

  const noteExcerpt = noteContent.length > 100
    ? noteContent.slice(0, 100) + '...'
    : noteContent;

  const seen = new Set<string>();
  const mentionIds: string[] = [];

  for (const r of recipients) {
    if (!r.userId || seen.has(r.userId)) continue;
    seen.add(r.userId);

    const mentionDoc: Omit<Mention, 'id'> = {
      tenantId: params.tenantId,
      mentionedUserId: r.userId,
      mentionedUserName: r.userName,
      createdBy: params.createdBy,
      createdByName: params.createdByName,
      entityType: params.entityType,
      entityId: params.entityId,
      entityTitle: params.entityTitle,
      noteContent,
      noteExcerpt,
      status: MentionStatus.UNREAD,
      createdAt: new Date().toISOString(),
      ...(params.linkPath ? { linkPath: params.linkPath } : {}),
      ...(params.sourceNoteId ? { sourceNoteId: params.sourceNoteId } : {}),
    };

    const id = await writeMentionDoc(mentionDoc, params.sourceNoteId);
    if (id) mentionIds.push(id);
  }

  return mentionIds;
}

/**
 * Mark a mention as read
 *
 * @param mentionId - Mention document ID
 */
export async function markMentionAsRead(mentionId: string): Promise<void> {
  await updateDoc(doc(db, 'mentions', mentionId), {
    status: MentionStatus.READ,
    readAt: new Date().toISOString(),
  });
}

/**
 * Mark EVERY unread mention on one entity as read — the cascade.
 *
 * Opening the thing you were tagged in is the act of reading it, so landing
 * on a fault / deal / task clears all the tags pointing at it, not just the
 * one row that was clicked. This is what stops a second tag on the same
 * fault sitting unread after you've already been and looked at it.
 *
 * Deliberately keyed on entityId ALONE (no entityType filter): entity ids are
 * unique across collections, and keeping the filter list short keeps this on
 * the (tenantId, mentionedUserId, entityId, status) composite index.
 *
 * @returns Number of mentions marked read
 */
export async function markEntityMentionsAsRead(
  userId: string,
  tenantId: string,
  entityId: string
): Promise<number> {
  if (!userId || !entityId) return 0;

  const q = query(
    collection(db, 'mentions'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('mentionedUserId', '==', userId),
    where('entityId', '==', entityId),
    where('status', '==', MentionStatus.UNREAD)
  );

  const snapshot = await getDocs(q);
  if (snapshot.empty) return 0;

  const readAt = new Date().toISOString();
  await Promise.all(
    snapshot.docs.map((docSnap) =>
      updateDoc(doc(db, 'mentions', docSnap.id), {
        status: MentionStatus.READ,
        readAt,
      })
    )
  );

  return snapshot.size;
}

/**
 * Mark all mentions as read for a user
 *
 * @param userId - User ID
 * @param tenantId - Tenant ID
 */
export async function markAllMentionsAsRead(
  userId: string,
  tenantId: string
): Promise<void> {
  const q = query(
    collection(db, 'mentions'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('mentionedUserId', '==', userId),
    where('status', '==', MentionStatus.UNREAD)
  );

  const snapshot = await getDocs(q);

  const promises = snapshot.docs.map((docSnap) =>
    updateDoc(doc(db, 'mentions', docSnap.id), {
      status: MentionStatus.READ,
      readAt: new Date().toISOString(),
    })
  );

  await Promise.all(promises);
}

/**
 * Archive a mention (soft delete — the "clear / done" action on the dashboard)
 *
 * Only mutates status. readAt is preserved so "when they first read it"
 * data is not lost (users can clear without having explicitly read first).
 *
 * @param mentionId - Mention document ID
 */
export async function archiveMention(mentionId: string): Promise<void> {
  await updateDoc(doc(db, 'mentions', mentionId), {
    status: MentionStatus.ARCHIVED,
  });
}

/**
 * Archive all non-archived mentions for a user (bulk "Clear all")
 *
 * @param userId - User ID
 * @param tenantId - Tenant ID
 * @returns Archived mention snapshots (id + prior status) for undo
 */
export async function archiveAllMentions(
  userId: string,
  tenantId: string
): Promise<{ id: string; priorStatus: MentionStatus }[]> {
  const q = query(
    collection(db, 'mentions'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('mentionedUserId', '==', userId),
    where('status', '!=', MentionStatus.ARCHIVED)
  );

  const snapshot = await getDocs(q);

  const snapshots = snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    priorStatus: (docSnap.data() as Mention).status,
  }));

  await Promise.all(
    snapshot.docs.map((docSnap) =>
      updateDoc(doc(db, 'mentions', docSnap.id), {
        status: MentionStatus.ARCHIVED,
      })
    )
  );

  return snapshots;
}

/**
 * Archive every mention the user has already READ ("Clear read").
 *
 * The read pile is the tail of the cascade: it stays visible under "Earlier"
 * until the user sweeps it, so nothing vanishes without them saying so.
 * Unread mentions are untouched.
 *
 * @returns Archived mention snapshots (id + prior status) for undo
 */
export async function archiveReadMentions(
  userId: string,
  tenantId: string
): Promise<{ id: string; priorStatus: MentionStatus }[]> {
  const q = query(
    collection(db, 'mentions'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('mentionedUserId', '==', userId),
    where('status', '==', MentionStatus.READ)
  );

  const snapshot = await getDocs(q);

  await Promise.all(
    snapshot.docs.map((docSnap) =>
      updateDoc(doc(db, 'mentions', docSnap.id), {
        status: MentionStatus.ARCHIVED,
      })
    )
  );

  return snapshot.docs.map((docSnap) => ({
    id: docSnap.id,
    priorStatus: MentionStatus.READ,
  }));
}

/**
 * Archive every live mention that points at one link, for EVERY recipient.
 *
 * Used when the thing a ping points at is finished for everyone — e.g. an
 * action item being cleared — so the ping drops off each person's plate and
 * bell instead of waiting to be ticked off by hand. Unread and read pings are
 * both archived; already-archived ones are left alone. Like `archiveMention`,
 * only status changes (readAt is kept).
 *
 * Served by the (tenantId, linkPath) composite index. No `!=` on status —
 * the ARCHIVED skip happens client-side so the index stays two fields.
 *
 * @returns Number of mentions archived
 */
export async function archiveMentionsByLinkPath(
  linkPath: string,
  tenantId: string
): Promise<number> {
  if (!linkPath) return 0;

  const q = query(
    collection(db, 'mentions'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('linkPath', '==', linkPath)
  );

  const snapshot = await getDocs(q);
  const live = snapshot.docs.filter(
    (docSnap) => (docSnap.data() as Mention).status !== MentionStatus.ARCHIVED
  );

  await Promise.all(
    live.map((docSnap) =>
      updateDoc(doc(db, 'mentions', docSnap.id), {
        status: MentionStatus.ARCHIVED,
      })
    )
  );

  return live.length;
}

/**
 * Restore an archived mention (undo archive). Defaults to READ since
 * the user has already seen the notification by the time they archive.
 *
 * @param mentionId - Mention document ID
 * @param restoreTo - Status to restore to (default: READ)
 */
export async function unarchiveMention(
  mentionId: string,
  restoreTo: MentionStatus = MentionStatus.READ
): Promise<void> {
  await updateDoc(doc(db, 'mentions', mentionId), {
    status: restoreTo,
  });
}

// ============================================================================
// MENTION QUERIES
// ============================================================================

/**
 * Get unread mention count for a user
 *
 * @param userId - User ID
 * @param tenantId - Tenant ID
 * @returns Number of unread mentions
 */
export async function getUnreadMentionCount(
  userId: string,
  tenantId: string
): Promise<number> {
  const q = query(
    collection(db, 'mentions'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('mentionedUserId', '==', userId),
    where('status', '==', MentionStatus.UNREAD)
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Get recent mentions for a user
 *
 * @param userId - User ID
 * @param tenantId - Tenant ID
 * @param limit - Max number to return
 * @returns Array of mentions, newest first
 */
export async function getRecentMentions(
  userId: string,
  tenantId: string,
  limit: number = 20
): Promise<Mention[]> {
  const q = query(
    collection(db, 'mentions'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('mentionedUserId', '==', userId),
    where('status', '!=', MentionStatus.ARCHIVED),
    orderBy('status'),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);

  return snapshot.docs
    .map((doc) => ({ id: doc.id, ...doc.data() } as Mention))
    .slice(0, limit);
}

/**
 * Subscribe to real-time mention updates for a user
 *
 * @param userId - User ID
 * @param tenantId - Tenant ID
 * @param callback - Callback with updated mentions
 * @returns Unsubscribe function
 */
export function subscribeMentions(
  userId: string,
  tenantId: string,
  callback: (mentions: Mention[]) => void
): () => void {
  // TWO listeners, deliberately — not one `status != ARCHIVED` query.
  //
  // The single-query version had to `orderBy('status')` first (Firestore
  // requires it alongside the inequality), and 'READ' sorts before 'UNREAD'
  // alphabetically. Its limit therefore filled with READ documents FIRST, so
  // once a user accumulated 200 active mentions their genuinely-unread ones
  // fell off the end and silently stopped appearing. Now that read mentions
  // deliberately linger in the "Earlier" pile instead of being cleared, that
  // was going to start biting.
  //
  // Splitting the piles gives each its own budget: unread is what the badge
  // counts and must never be truncated in practice, while the read tail is
  // history and is capped tight. Both queries are equality-on-status +
  // orderBy(createdAt), served by the existing
  // (tenantId, mentionedUserId, status, createdAt DESC) composite index.
  const forStatus = (status: MentionStatus, cap: number) =>
    query(
      collection(db, 'mentions'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId)),
      where('mentionedUserId', '==', userId),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      limit(cap)
    );

  let unread: Mention[] = [];
  let read: Mention[] = [];

  const emit = () => callback([...unread, ...read]);
  const toMentions = (snapshot: { docs: { id: string; data: () => unknown }[] }) =>
    snapshot.docs.map((d) => ({ id: d.id, ...(d.data() as object) } as Mention));

  const unsubUnread = onSnapshot(forStatus(MentionStatus.UNREAD, 200), (snap) => {
    unread = toMentions(snap);
    emit();
  });
  const unsubRead = onSnapshot(forStatus(MentionStatus.READ, 50), (snap) => {
    read = toMentions(snap);
    emit();
  });

  return () => {
    unsubUnread();
    unsubRead();
  };
}
